import type { AgentFrame } from './agent-socket';
import type { Message } from './agent-api';

// ChatItem is one rendered row of the transcript.
//  - user:      a prompt the user sent (right-aligned bubble)
//  - assistant: Claude's prose reply (left-aligned bubble); consecutive
//               assistant text is merged into one item
//  - tool:      a tool invocation rendered as a compact card (name + input)
//  - error:     a turn/stream error rendered as an error bubble
export type ChatItem =
  | { kind: 'user'; text: string }
  | { kind: 'assistant'; text: string }
  | { kind: 'tool'; name: string; input?: unknown }
  | { kind: 'error'; text: string };

// ChatState is the full rendered conversation plus the live busy flag.
export interface ChatState {
  items: ChatItem[];
  busy: boolean;
}

export function emptyState(): ChatState {
  return { items: [], busy: false };
}

// reduce folds one incoming WS frame into the transcript, returning a NEW state
// (pure — never mutates the input). Frame handling:
//  - user           → append a user item
//  - assistant_text → append an assistant item, OR merge into the previous item
//                     when it is also assistant text (joined with a blank line)
//  - tool_use       → append a tool item (this also breaks assistant-text merging)
//  - state          → update busy
//  - turn_done      → clear busy
//  - error          → append an error item AND clear busy (the turn is over)
export function reduce(state: ChatState, frame: AgentFrame): ChatState {
  switch (frame.type) {
    case 'user':
      return { ...state, items: [...state.items, { kind: 'user', text: frame.text }] };
    case 'assistant_text': {
      const last = state.items[state.items.length - 1];
      if (last && last.kind === 'assistant') {
        const merged: ChatItem = { kind: 'assistant', text: `${last.text}\n\n${frame.text}` };
        return { ...state, items: [...state.items.slice(0, -1), merged] };
      }
      return { ...state, items: [...state.items, { kind: 'assistant', text: frame.text }] };
    }
    case 'tool_use':
      return { ...state, items: [...state.items, { kind: 'tool', name: frame.name, input: frame.input }] };
    case 'state':
      return { ...state, busy: frame.busy };
    case 'turn_done':
      return { ...state, busy: false };
    case 'error':
      return { ...state, busy: false, items: [...state.items, { kind: 'error', text: frame.message }] };
    default:
      return state;
  }
}

// seedFromHistory builds the initial transcript from getAgent()'s stored
// Message[] by folding each part through `reduce` as the equivalent frame, so
// history and live streaming produce identical rendering (including assistant
// text merging). busy is left false; the WS 'state' frame sets it on connect.
export function seedFromHistory(messages: Message[]): ChatState {
  let state = emptyState();
  for (const m of messages) {
    for (const p of m.parts) {
      if (p.kind === 'text') {
        const text = p.text ?? '';
        state = reduce(state, m.role === 'user' ? { type: 'user', text } : { type: 'assistant_text', text });
      } else if (p.kind === 'tool') {
        state = reduce(state, { type: 'tool_use', name: p.name ?? '', input: p.input });
      }
    }
  }
  return state;
}
